import { RiAddLine, RiCheckLine } from "react-icons/ri";
import { LimeInputLabel, LimeInputLabelProps } from "./LimeInputLabel";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";
import { LimeInput } from "./LimeInput";
import { useDebouncedCallback } from "@mantine/hooks";
import { HexColorPicker } from "react-colorful";
import { cn } from "@/utils";

const colors = [
  "#E9B2BF",
  "#EFE8CB",
  "#84D39C",
  "#DAE1F1",
  "#FFD964",
  "#B2DFE9",
];

export const LimeColorPicker = ({
  value,
  onChange,
  wrapperClassName,
  ...props
}: {
  value?: string;
  onChange: (color: string) => void;
  wrapperClassName?: string;
} & LimeInputLabelProps) => {
  const isCustomColor = value ? !colors.includes(value) : false;

  const debounceCustomColor = useDebouncedCallback(onChange, 100);

  return (
    <div className={cn("flex flex-col", wrapperClassName)}>
      <LimeInputLabel {...props} />

      <div className="flex justify-between gap-2">
        {colors.map((color) => (
          <CustomColorSwatch key={color} value={color} onChange={onChange}>
            {color === value && "✓"}
          </CustomColorSwatch>
        ))}

        <Popover>
          <PopoverTrigger>
            <Button
              className="flex h-10 w-full items-center justify-center rounded-full"
              isIconOnly
              type="button"
              onPress={() => (isCustomColor ? undefined : onChange("#F5F5F5"))}
              style={{ backgroundColor: isCustomColor ? value : "#F5F5F5" }}
              aria-label={isCustomColor ? "Custom color" : "Add custom color"}
            >
              {isCustomColor ? <RiCheckLine /> : <RiAddLine />}
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <div className="flex flex-col gap-2 px-1 py-2">
              <HexColorPicker
                color={value && isCustomColor ? value : "#F5F5F5"}
                onChange={debounceCustomColor}
              />

              <LimeInput
                maxLength={7}
                value={value && isCustomColor ? value : "#F5F5F5"}
                onValueChange={onChange}
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

const CustomColorSwatch = ({
  value,
  onChange,
  children,
}: {
  value: string;
  onChange: (color: string) => void;
  children: React.ReactNode;
}) => {
  return (
    <Button
      isIconOnly
      className="flex h-10 w-full items-center justify-center rounded-full"
      type="button"
      onPress={() => onChange(value)}
      style={{ backgroundColor: value }}
      aria-label={`Select color ${value}`}
    >
      {children}
    </Button>
  );
};
